import { Filter, Plus, RefreshCw, X } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/v2/ui/primitives/badge";
import { Button } from "@/v2/ui/primitives/button";
import { Input } from "@/v2/ui/primitives/input";

export interface FilterItem {
  filter: string | string[] | object;
  filterBy: string;
}

export interface PageHeaderProps {
  title: string;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchClear?: () => void;
  onFilterClick?: () => void;
  onAddClick?: () => void;
  onRefresh?: () => void;
  filters?: FilterItem[];
  onRemoveFilter?: (index: number) => void;
  extraContent?: React.ReactNode;
  className?: string;
}

const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      title,
      searchValue = "",
      searchPlaceholder = "Search...",
      onSearchChange,
      onSearchClear,
      onFilterClick,
      onAddClick,
      onRefresh,
      filters = [],
      onRemoveFilter,
      extraContent,
      className,
    },
    ref
  ) => {
    const formatFilterValue = (filter: FilterItem): string => {
      const { filter: value, filterBy } = filter;
      const label = filterBy
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase();

      if (Array.isArray(value)) {
        return `${label}: ${value.map((f) => String(f).replace(/_/g, " ")).join(", ")}`;
      }
      if (typeof value === "object" && value !== null) {
        const obj = value as { ids?: string[]; match?: string };
        return `${label}: ${(obj.ids || []).join(", ")}${obj.match ? ` (${obj.match})` : ""}`;
      }
      return `${label}: ${String(value).replace(/_/g, " ")}`;
    };

    return (
      <div ref={ref} className={cn("tw-space-y-4", className)}>
        <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <h1 className="tw-text-2xl tw-font-bold tw-tracking-tight">
            {title}
          </h1>

          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-relative tw-flex tw-items-center">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
                className="tw-w-full sm:tw-w-[300px] tw-pr-10"
              />
              {searchValue && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="tw-absolute tw-right-1 tw-h-7 tw-w-7"
                  onClick={onSearchClear}
                >
                  <X className="tw-h-4 tw-w-4" />
                </Button>
              )}
            </div>

            {onRefresh && (
              <Button variant="outline" size="icon" onClick={onRefresh}>
                <RefreshCw className="tw-h-4 tw-w-4" />
              </Button>
            )}
            {onFilterClick && (
              <Button variant="outline" size="icon" onClick={onFilterClick}>
                <Filter className="tw-h-4 tw-w-4" />
              </Button>
            )}
            {onAddClick && (
              <Button variant="outline" size="icon" onClick={onAddClick}>
                <Plus className="tw-h-4 tw-w-4" />
              </Button>
            )}
            {extraContent}
          </div>
        </div>

        {filters && filters.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {filters.map(
              (filter) =>
                filter.filter && (
                  <Badge
                    key={filter.filterBy}
                    variant="secondary"
                    className="tw-flex tw-items-center tw-gap-1 tw-py-1 tw-px-3"
                  >
                    <span className="tw-text-xs">
                      {formatFilterValue(filter)}
                    </span>
                    {onRemoveFilter && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="tw-h-4 tw-w-4 tw-p-0 tw-ml-1 hover:tw-bg-transparent"
                        onClick={() => onRemoveFilter(filters.indexOf(filter))}
                      >
                        <X className="tw-h-3 tw-w-3" />
                      </Button>
                    )}
                  </Badge>
                )
            )}
          </div>
        )}
      </div>
    );
  }
);

PageHeader.displayName = "PageHeader";

export { PageHeader };
